import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { WayToPayEnums } from 'src/app/enum/way-to-pay.enums';
import { WayToPayModel } from 'src/app/models/management/way-to-pay-model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { WayToPayService } from 'src/app/services/management/way-to-pay.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-way-to-pay',
  templateUrl: './register-way-to-pay.component.html',
  styleUrls: ['./register-way-to-pay.component.scss'],
  standalone: false
})
export class RegisterWayToPayComponent implements OnInit {

  branchId: number = 0;
  disabledForm: boolean = false;
  formWayToPay: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Formas de Pagos';
  output$ = new Subject<any>();
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  url: string = 'way-to-pay';
  wayToPayContract: WayToPayModel = new WayToPayModel();
  wayToPayId: number = 0;

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private wayToPayService: WayToPayService,
    private windowService: WindowService) {
    this.validateForm(new WayToPayModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.wayToPayId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeCode(code: number) {
    await this.getWayToPayByCode(code);
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear formas de pagos.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.resetContract();
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar formas de pagos.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.wayToPayId) {
      this.toastHandlerService.showToast('warning', 'Debe crear una forma de pago.');
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la forma de pago?')) {
      try {
        await firstValueFrom(this.wayToPayService.deleteWayToPay(this.wayToPayId));
        this.createNewRegister();
        this.toastHandlerService.showToast('success', 'La forma de pago fue eliminada exitosamente.');
      } catch (error) {
        console.error('Error al eliminar la forma de pago.', error);
      }
    }
  }

  async getButtonClick(buttonClick: any) {
    this.handleWayToPayNavigation(buttonClick)
  }

  async getWayToPayByCode(code: number) {
    try {
      const response = await firstValueFrom(this.wayToPayService.getWayToPayByCode(code));
      this.wayToPayId = response.wayToPayId;
      this.wayToPayContract = response;
      this.validateForm(this.wayToPayContract);
    } catch (error) {
      console.error('Error al obtener la forma de pago por código.', error);
    }
  }

  async getWayToPayById() {
    try {
      const response = await firstValueFrom(this.wayToPayService.getWayToPayById(this.wayToPayId));
      this.wayToPayContract = response;
      this.validateForm(this.wayToPayContract);
    } catch (error) {
      console.error('Error al obtener la forma de pago por id.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async handleInitialState() {
    if (this.wayToPayId) {
      await this.getWayToPayById();
    } else {
      this.createNewRegister();
    }
  }

  async handleWayToPayNavigation(buttonClick: any) {
    const code = this.formWayToPay.get("code")?.value;
    const actionMap: Record<string, () => Promise<WayToPayModel>> = {
      first: () =>
        firstValueFrom(this.wayToPayService.getFirstIncomeConceptByKeys()),
      previus: () =>
        firstValueFrom(this.wayToPayService.getPreviusIncomeConceptByKeys(code)),
      next: () =>
        firstValueFrom(this.wayToPayService.getNextIncomeConceptByKeys(code)),
      last: () =>
        firstValueFrom(this.wayToPayService.getLastIncomeConceptByKeys()),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const wayToPay = await action();
      this.wayToPayId = wayToPay.wayToPayId;
      this.wayToPayContract = wayToPay;
      this.validateForm(this.wayToPayContract);
      this.location.replaceState(`/${this.url}/register/${this.wayToPayId}`);
    } catch (error) {
      console.error(`Error al buscar el concepto de ingreso (${buttonClick.target.id}).`, error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formWayToPay.valid) {
      const modelWayToPay = this.formWayToPay.getRawValue() as WayToPayModel;
      if (this.wayToPayId == 0) {
        try {
          const response = await firstValueFrom(this.wayToPayService.createWayToPay(modelWayToPay));
          this.wayToPayId = response.wayToPayId;
          this.wayToPayContract = response;
          this.validateForm(this.wayToPayContract);
          this.location.replaceState(`/${this.url}/register/${this.wayToPayId}`);
          this.toastHandlerService.showToast('success', `La forma de pago fue creada exitosamente.`);
        } catch (error) {
          console.error('Error al crear la forma de pago.', error);
        }
      } else {
        this.wayToPayContract.code = modelWayToPay.code;
        this.wayToPayContract.description = modelWayToPay.description;
        this.wayToPayContract.account = modelWayToPay.account;
        try {
          if (this.wayToPayContract.code == WayToPayEnums.anticipos || this.wayToPayContract.code == WayToPayEnums.efectivo ||
            this.wayToPayContract.code == WayToPayEnums.cheque || this.wayToPayContract.code == WayToPayEnums.transferencia) {
            const accepted = await this.confirmDialogService.confirm('Se recomienda no modificar las formas de pago con codigo 0-3, ya que son formas de pago que crea el sistema por defecto.¿Realmente desea modificar esta forma de pago?');
            if (!accepted) return;
          }
          const response = await firstValueFrom(this.wayToPayService.updateWayToPay(this.wayToPayContract));
          this.wayToPayContract = response;
          this.validateForm(this.wayToPayContract);
          this.toastHandlerService.showToast('success', `La forma de pago fue modificada exitosamente.`);
        } catch (error) {
          console.error('Error al actualizar la forma de pago.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.'
      },
      description: {
        required: 'El campo Descripción es obligatorio.',
        maxlength: 'La Descripción no puede tener más de 70 caracteres.'
      },
      account: {
        maxlength: 'La Cuenta no puede tener más de 15 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formWayToPay.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  resetContract() {
    this.wayToPayId = 0;
    this.wayToPayContract = new WayToPayModel();
    this.validateForm(this.wayToPayContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => {
        this.formWayToPay.enable();
        const controlCode = this.formWayToPay.controls['code'];
        this.wayToPayId != 0 ? controlCode.enable() : controlCode.disable();
      });
    } else {
      this.disabledForm = true;
      setTimeout(() => {
        this.formWayToPay.disable();
        this.formWayToPay.controls["code"].enable();
      });
    }
  }

  validateForm(wayToPay: WayToPayModel) {
    this.formWayToPay = this.formBuilder.group({
      code: [wayToPay.code, [Validators.required]],
      description: [wayToPay.description, [Validators.required, Validators.maxLength(70)]],
      account: [wayToPay.account, [Validators.maxLength(15)]]
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formWayToPay.controls).forEach((key) => {
      const control = this.formWayToPay.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
